/**
 * Template Library — the registry.
 *
 * Every hand-authored template in the library is listed here once. This module
 * is the only thing the admin preview page and the import call need to know
 * about; adding a template is a one-line change.
 *
 * `buildTemplateDoc` converts a built template into the exact document shape
 * the backend's Template model stores, so the browser never has to reshape it
 * and the import endpoint never has to guess.
 */

import luxuryListing from './flyer/real-estate/01-luxury-listing';
import editorialSplit from './flyer/real-estate/02-editorial-split';
import swissOpenHouse from './flyer/real-estate/03-swiss-open-house';
import emeraldEstate from './flyer/real-estate/04-emerald-estate';
import warmJustSold from './flyer/real-estate/05-warm-just-sold';
import eliteJustListed from './elite/01-just-listed';
import eliteOpenHouse from './elite/02-open-house';
import eliteJustSold from './elite/03-just-sold';
import eliteModernLuxury from './elite/04-modern-luxury';
import eliteDreamHome from './elite/05-dream-home';

/**
 * Ordered so the preview page reads as a considered set, not a dump.
 *
 * Templates belong to a COLLECTION. A collection is either a varied set (each
 * template its own palette and voice) or a branded series (one identity across
 * every sheet, the way an agency's print pack works). The quality gate applies
 * different uniqueness rules to each, which is why `meta.collection` and
 * `meta.archetype` are required.
 */
export const ALL_TEMPLATES = [
  eliteJustListed,
  eliteOpenHouse,
  eliteJustSold,
  eliteModernLuxury,
  eliteDreamHome,
  luxuryListing,
  editorialSplit,
  swissOpenHouse,
  emeraldEstate,
  warmJustSold,
];

/** Collections that share one brand identity by design. */
export const BRANDED_COLLECTIONS = new Set(['elite-realty']);

/** Group the registry by collection, for the preview page. */
export const groupedTemplates = () => {
  const groups = new Map();
  ALL_TEMPLATES.forEach((tpl) => {
    const key = `${tpl.meta.collection} · ${tpl.meta.documentType} · ${tpl.meta.subCategory}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(tpl);
  });
  return [...groups.entries()].map(([label, templates]) => ({ label, templates }));
};

/**
 * Build a template and shape it for storage.
 *
 * `status: 'pending'` and `source: 'ai_generated'` put the record straight into
 * the existing admin review queue at /admin/ai-templates — nothing is published
 * without a human approving it there.
 */
export const buildTemplateDoc = (tpl) => {
  const built = tpl.build();
  const firstPage = built.pages[0];

  return {
    library_id: tpl.id,
    name: tpl.meta.name,
    description: tpl.meta.description,
    canvas_width: built.canvas.width,
    canvas_height: built.canvas.height,
    canvas_background: firstPage.background,
    elements: [],
    template_json: {
      canvas_width: built.canvas.width,
      canvas_height: built.canvas.height,
      pages: built.pages,
    },
    tags: tpl.meta.tags,
    is_premium: true,
    source: 'ai_generated',
    status: 'pending',
  };
};

/** Shape a built template for the editor's `location.state.template` contract. */
export const toEditorTemplate = (tpl) => {
  const built = tpl.build();
  return {
    name: tpl.meta.name,
    canvas_width: built.canvas.width,
    canvas_height: built.canvas.height,
    template_json: {
      canvas_width: built.canvas.width,
      canvas_height: built.canvas.height,
      pages: built.pages,
    },
  };
};
